import { FC } from 'react';
import { DisputesPageIcon, PaperRollIcon, RedirectIcon, RefundIcon } from 'assets/icons';
import { AdjustmentDisputeDetails, AdjustmentRefundDetails, AdjustmentTransactionDetails } from '../payrun.model';
import { CustomAvatar } from 'components';
import { getInitials, openInNewTab } from 'utils/commonUtils';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { SuggestedAdjustmentType } from '../constants';
import { getAmountWithCurrency } from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import routesPath from 'routes/RoutesPath';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { getReceiptStatusColor } from 'containers/receipts/util';

interface SuggestedAdjustmentDataCardProps {
  details: AdjustmentDisputeDetails | AdjustmentRefundDetails | AdjustmentTransactionDetails;
  type: SuggestedAdjustmentType;
  className?: string;
}

const SuggestedAdjustmentDataCard: FC<SuggestedAdjustmentDataCardProps> = ({ details, type, className = '' }) => {
  const { id: teamId } = useAppSelector(selectCurrentTeam);

  const handleViewDetails = () => {
    if (type === SuggestedAdjustmentType.DISPUTE) {
      const dispute = details as AdjustmentDisputeDetails;
      openInNewTab(
        routesPath.DISPUTE_BY_ID.replace(':rootDisputeId', dispute.disputeId).replace(':teamId', teamId.toString())
      );
    }
    if (type === SuggestedAdjustmentType.REFUND) {
      const refund = details as AdjustmentRefundDetails;
      openInNewTab(routesPath.RECEIPT_BY_ID.replace(':rootReceiptId', refund.ID).replace(':teamId', teamId.toString()));
    }
    const transaction = details as AdjustmentTransactionDetails;
    openInNewTab(
      routesPath.RECEIPT_BY_ID.replace(':rootReceiptId', transaction.id).replace(':teamId', teamId.toString())
    );
  };

  const getTitle = () => {
    if (type === SuggestedAdjustmentType.DISPUTE) {
      const dispute = details as AdjustmentDisputeDetails;
      return `Dispute ${dispute.disputeId}`;
    }
    if (type === SuggestedAdjustmentType.REFUND) {
      const refund = details as AdjustmentRefundDetails;
      return `Refund ${refund.ID}`;
    }
    const transaction = details as AdjustmentTransactionDetails;
    return `Payment ${transaction.id}`;
  };

  const getFormattedAmount = () => {
    if (type === SuggestedAdjustmentType.DISPUTE) {
      const dispute = details as AdjustmentDisputeDetails;
      return getAmountWithCurrency(dispute.disputeAmount);
    }
    if (type === SuggestedAdjustmentType.REFUND) {
      const refund = details as AdjustmentRefundDetails;
      return getAmountWithCurrency(refund.amount);
    }
    const transaction = details as AdjustmentTransactionDetails;
    return getAmountWithCurrency(transaction.amount);
  };

  const getReason = () => {
    if (type === SuggestedAdjustmentType.DISPUTE) {
      const dispute = details as AdjustmentDisputeDetails;
      return dispute.reason;
    }
    if (type === SuggestedAdjustmentType.REFUND) {
      const refund = details as AdjustmentRefundDetails;
      return refund.reason;
    }
    const transaction = details as AdjustmentTransactionDetails;
    return transaction.reason;
  };

  const getFormattedDate = () => {
    if (type === SuggestedAdjustmentType.DISPUTE) {
      const dispute = details as AdjustmentDisputeDetails;
      if (dispute.createdAt) {
        return DateUtils.getDateInFormat({ date: new Date(dispute.createdAt), dateFormat: 'dd MMM yyyy' });
      }
    }
    if (type === SuggestedAdjustmentType.REFUND) {
      const refund = details as AdjustmentRefundDetails;
      if (refund.date) {
        return DateUtils.getDateInFormat({ date: new Date(refund.date), dateFormat: 'dd MMM yyyy' });
      }
    }
    const transaction = details as AdjustmentTransactionDetails;
    if (transaction.date) {
      return DateUtils.getDateInFormat({ date: new Date(transaction.date), dateFormat: 'dd MMM yyyy' });
    }

    return '-';
  };

  const getStatusText = () => {
    if (type === SuggestedAdjustmentType.DISPUTE) {
      const dispute = details as AdjustmentDisputeDetails;
      if (dispute.status) {
        return (
          <div
            className={`text-sm capitalize ${
              details.status.toLowerCase() === 'lost' ? 'text-error' : 'text-successHighlight'
            }`}>
            {details.status.toLowerCase()}
          </div>
        );
      }
    }
    if (type === SuggestedAdjustmentType.REFUND) {
      const refund = details as AdjustmentRefundDetails;
      if (refund.status) {
        return <div className={'text-sm capitalize text-tertiaryText'}>{refund.status.toLowerCase()}</div>;
      }
    }
    const transaction = details as AdjustmentTransactionDetails;
    if (transaction.status) {
      return (
        <div className={'text-sm capitalize'} style={{ color: getReceiptStatusColor(transaction.status) }}>
          {transaction.status.toLowerCase()}
        </div>
      );
    }

    return '-';
  };

  const getIcon = () => {
    if (type === SuggestedAdjustmentType.DISPUTE) {
      return <DisputesPageIcon className="path-fill-current text-tertiaryText" />;
    }
    if (type === SuggestedAdjustmentType.REFUND) {
      return <RefundIcon className="path-stroke-current text-tertiaryText" />;
    }
    return <PaperRollIcon className="path-fill-curren" />;
  };

  return (
    <div className={`rounded-lg border border-borderGray bg-[#ECF1F733] p-4 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-secondaryBtn">
            <div className="flex h-5 w-5 items-center justify-center">{getIcon()}</div>
          </div>
          <div>
            <div className="text-sbase font-semibold text-tertiaryText">{getTitle()}</div>
            {getReason() && <div className="text-sm text-primaryText">Reason: {getReason()}</div>}
            <div className="mt-1 flex items-center gap-2 text-sm text-accentText">
              <span>{getFormattedDate()}</span>
              <span className="text-sm font-normal text-accentText">•</span>
              <TooltipWrapper tooltip={details.merchantName}>
                <CustomAvatar
                  src={details.merchantProfilePictureURL}
                  label={getInitials(details.merchantName)}
                  className="h-4 w-4"
                  fontStyle="text-[8px]"
                />
              </TooltipWrapper>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="flex flex-col items-end">
            <span className="text-base font-semibold text-primaryText">{getFormattedAmount()}</span>
            <div className="text-sm capitalize text-primaryText">
              {details.paymentNetwork} {details.cardLast4}
            </div>
            {getStatusText()}
          </div>
          <div>
            <button
              onClick={handleViewDetails}
              className="flex h-5 w-5 items-center justify-center focus:outline-none"
              aria-label="View details"
              tabIndex={0}>
              <RedirectIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestedAdjustmentDataCard;
